/**
 * QuickLookDialog — the reference's quick look: gallery on the left, buy panel on the
 * right, inside a `Dialog`.
 *
 * Ported 1:1 from `tw-components.jsx`. Deviations are mechanical only: imports instead
 * of file-scope helpers, and `React` for its hooks.
 *
 * **This is not "the PDP in a modal."** The point is that it's cheap: one image stage
 * with thumbnails, brand / name / sku / stock / price / unit, an optional description,
 * and a stepper plus add-to-cart that closes the dialog. If you find yourself wanting
 * tabs, related products or reviews in here, the user wants the PDP — send them there
 * with the card's `onClick` instead.
 *
 * `product` is a view model, not a NetSuite record; the caller maps a domain object onto
 * it. Everything except `name` and `price` is optional, which is what lets this app omit
 * `rating`, `reviews` and `description` — none of which the listing payload carries.
 *
 * | Field                          | Notes                                              |
 * | ------------------------------ | -------------------------------------------------- |
 * | `images`                       | URLs. More than one renders the thumbnail strip     |
 * | `views`                        | icon-name fallback when there are no images at all  |
 * | `isSaved` / `onFavorite(next)` | omit `onFavorite` to hide the heart                |
 * | `onAutoDelivery(qty)`          | optional subscription CTA; omit to hide it         |
 * | `stock`                        | `"in"` / `"low"` / `"out"`                         |
 *
 * Unlike `ProductCard` this keeps upstream's `disabled={p.stock === 'out'}`. The card's
 * was relaxed because a backorderable item must stay addable from a listing; inside a
 * deliberately opened quick look the disabled state is a useful signal and the PDP is
 * still one click away. Say so if that reads wrong and it can come out.
 */
import React from "react";

import { AddToFavorite } from "./AddToFavorite.jsx";
import { Badge } from "./Badge.jsx";
import { PC_ADD_TRANSITION, PC_ICON_SPRING, STOCK } from "./constants";
import { cx } from "./cx";
import { Dialog } from "./Dialog.jsx";
import { Icon } from "./Icon.jsx";
import { PriceTag } from "./PriceTag.jsx";
import { QuantityStepper } from "./QuantityStepper.jsx";
import { Rating } from "./Rating.jsx";

export function QuickLookDialog({ open, onClose, product, onAdd }) {
  const [view, setView] = React.useState(0);
  const [qty, setQty] = React.useState(1);
  const key = product ? product.sku || product.name : null;
  React.useEffect(() => {
    setView(0);
    setQty(1);
  }, [open, key]);
  if (!open || !product) return null;
  const p = product;
  const st = STOCK[p.stock || "in"] || STOCK.in;
  const onSale = p.originalPrice !== undefined && p.originalPrice > p.price;
  let views = (
    p.images && p.images.length ? p.images : p.image ? [p.image] : []
  ).map((src) => ({ src }));
  if (!views.length && p.views && p.views.length) {
    views = p.views.map((ic) => ({ icon: ic }));
  }
  if (!views.length) views.push({ icon: p.icon || "medical_services" });
  const cur = views[Math.min(view, views.length - 1)];
  return (
    <Dialog open={open} onClose={onClose} size="lg" className="dnd-ql">
      <div className="dnd-ql__grid grid grid-cols-[300px_1fr] gap-[var(--space-7)] pb-[var(--space-7)] max-[720px]:grid-cols-1">
        <div className="dnd-ql__gallery flex flex-col gap-[var(--space-3)] min-w-0">
          <div className="dnd-ql__stage relative aspect-square rounded-lg bg-neutral-50 border border-line-subtle overflow-hidden flex items-center justify-center">
            {cur.src ? (
              <img
                key={view}
                src={cur.src}
                alt={p.name}
                className="w-full h-full object-cover animate-[dnd-overlay-in_var(--duration-base)_var(--ease-out)]"
              />
            ) : (
              <Icon
                key={view}
                name={cur.icon}
                size={96}
                weight={200}
                className="text-neutral-300 animate-[dnd-overlay-in_var(--duration-base)_var(--ease-out)]"
              />
            )}
            <div className="absolute top-[var(--space-3)] left-[var(--space-3)] flex gap-[6px]">
              {onSale && (
                <Badge color="solid" size="sm">
                  Sale
                </Badge>
              )}
              {p.badge && (
                <Badge color="ocean" size="sm">
                  {p.badge}
                </Badge>
              )}
            </div>
            {p.onFavorite && (
              <AddToFavorite
                saved={!!p.isSaved}
                onToggle={p.onFavorite}
                className="dnd-ql__fav absolute top-[var(--space-2)] right-[var(--space-2)]"
              />
            )}
          </div>
          {views.length > 1 && (
            <div className="dnd-ql__thumbs flex gap-[var(--space-2)]">
              {views.map((v, i) => (
                <button
                  key={i}
                  onClick={() => setView(i)}
                  aria-label={`View image ${i + 1} of ${p.name}`}
                  className={cx(
                    "w-[56px] h-[56px] p-0 rounded-md border-2 bg-neutral-50 overflow-hidden cursor-pointer inline-flex items-center justify-center [transition:border-color_var(--duration-fast),box-shadow_var(--duration-fast)]",
                    i === view
                      ? "border-orange-500 [box-shadow:0_0_0_4px_var(--orange-100)]"
                      : "border-line-subtle hover:border-line"
                  )}
                >
                  {v.src ? (
                    <img
                      src={v.src}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Icon
                      name={v.icon}
                      size={26}
                      weight={200}
                      className="text-neutral-300"
                    />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="dnd-ql__info flex flex-col gap-[var(--space-2)] min-w-0">
          {p.brand && (
            <span className="text-2xs font-bold tracking-[var(--tracking-wider)] uppercase text-[var(--text-accent)]">
              {p.brand}
            </span>
          )}
          <h2 className="text-2xl font-bold text-ink leading-[1.25] m-0 tracking-[var(--tracking-tight)]">
            {p.name}
          </h2>
          {p.sku && <span className="text-xs text-ink-3">{p.sku}</span>}
          {p.rating !== undefined && (
            <Rating
              value={p.rating}
              count={p.reviews}
              size={16}
              readOnly
              style={{ marginTop: 2 }}
            />
          )}
          <span
            className={cx(
              "inline-flex items-center gap-[var(--space-1)] text-xs font-medium",
              st.cls
            )}
          >
            <Icon name={st.icon} size={14} weight={500} fill />
            {st.label}
          </span>
          <div className="flex flex-col gap-[1px] mt-[var(--space-2)]">
            <PriceTag
              amount={p.price}
              original={p.originalPrice}
              size="md"
              className="flex-wrap"
            />
            {p.unit && (
              <span className="text-xs text-ink-2 font-medium">{p.unit}</span>
            )}
          </div>
          {p.description && (
            <p className="text-sm text-ink-2 leading-[1.6] mt-[var(--space-2)] mb-0 [text-wrap:pretty]">
              {p.description}
            </p>
          )}
          <div className="dnd-ql__actions flex items-center gap-[var(--space-3)] mt-[var(--space-4)]">
            <QuantityStepper value={qty} onChange={setQty} min={1} />
            <button
              disabled={p.stock === "out"}
              onClick={() => {
                if (onAdd) onAdd(qty);
                if (onClose) onClose();
              }}
              className={cx(
                "group/qla flex-1 inline-flex items-center justify-center gap-[var(--space-2)] h-[44px] border-0 rounded-full bg-orange-500 text-white cursor-pointer font-sans font-semibold text-sm",
                PC_ADD_TRANSITION,
                "enabled:hover:bg-orange-600 enabled:hover:shadow-brand enabled:active:scale-[0.97] disabled:bg-neutral-200 disabled:text-[var(--text-disabled)] disabled:cursor-not-allowed disabled:shadow-none"
              )}
            >
              <Icon
                name="add_shopping_cart"
                size={20}
                weight={400}
                className={cx(
                  PC_ICON_SPRING,
                  "group-hover/qla:rotate-[-9deg] group-hover/qla:scale-[1.08]"
                )}
              />
              {qty > 1 ? `Add ${qty} to cart` : "Add to cart"}
            </button>
          </div>
          {p.onAutoDelivery && (
            <div className="flex flex-col gap-[6px] mt-[var(--space-2)]">
              <button
                onClick={() => p.onAutoDelivery(qty)}
                className="dnd-ql__sched inline-flex items-center justify-center gap-[var(--space-2)] w-full h-[40px] bg-transparent border-[1.5px] border-line rounded-full text-blue-700 font-sans font-semibold text-sm cursor-pointer [transition:background_var(--duration-fast),border-color_var(--duration-fast)] hover:bg-soft hover:border-blue-300"
              >
                <Icon
                  name={p.onStandingOrder ? "swap_horiz" : "autorenew"}
                  size={18}
                  weight={500}
                />
                {p.onStandingOrder
                  ? "Move this auto-delivery"
                  : "Set up auto-delivery"}
              </button>
              <p className="text-xs text-ink-3 text-center m-0">
                {p.onStandingOrder
                  ? "Already on your " +
                    (p.standingOrderName || "auto-delivery") +
                    " — move it to another order, or a new one."
                  : "Add it to a scheduled order — or start a new one."}
              </p>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}
